import React from "react";
import {
  Layers, Receipt, Calendar, ShoppingCart, MessageSquare, Shield, BarChart3, Bell, Tag, Navigation, User, Camera, Car, ClipboardList, FileText, Home, Image, Landmark, Store, Truck,
} from "lucide-react";

// Identificadores dos 3 perfis. Persistido no localStorage como string;
// usado pra rotear entre painéis (locatário/locador/freteiro).
export const PERFIS = {
  LOCATARIO: 'locatario',
  LOCADOR: 'locador',
  FRETEIRO: 'freteiro',
};

// Estado inicial do KYC quando o usuário ainda não tem nada validado.
export const KYC_STATUS_PADRAO = {
  locatario: { aprovado: false, pendente: false, documentos: {} },
  locador:   { aprovado: false, pendente: false, documentos: {} },
  freteiro:  { aprovado: false, pendente: false, documentos: {} },
};

// Requisitos de KYC por perfil — usados pelo modal "Iniciar Verificação"
// e pela barra de progresso. `obrigatorio` define se entra no cálculo de %.
export const KYC_REQUISITOS = {
  locatario: {
    label: 'Particular',
    descricao: 'Alugue e anuncie produtos de forma segura',
    documentos: [
      { id: 'cpf', label: 'CPF válido', icon: <FileText size={20} />, obrigatorio: true },
      { id: 'selfie', label: 'Selfie com documento', icon: <Camera size={20} />, obrigatorio: true },
      { id: 'endereco', label: 'Comprovante de endereço', icon: <Home size={20} />, obrigatorio: false },
    ],
    beneficios: ['Alugar produtos', 'Favoritar itens', 'Histórico de locações'],
  },
  locador: {
    label: 'Locador',
    descricao: 'Anuncie e gerencie seus produtos',
    documentos: [
      { id: 'cpf_cnpj', label: 'CPF ou CNPJ', icon: <FileText size={20} />, obrigatorio: true },
      { id: 'selfie_doc', label: 'Selfie com documento', icon: <Camera size={20} />, obrigatorio: true },
      { id: 'endereco', label: 'Comprovante de endereço', icon: <Home size={20} />, obrigatorio: true },
      { id: 'conta_bancaria', label: 'Dados bancários para recebimento', icon: <Landmark size={20} />, obrigatorio: true },
    ],
    beneficios: ['Publicar anúncios', 'Receber pagamentos', 'Dashboard de vendas', 'Gestão de devoluções'],
  },
  freteiro: {
    label: 'Freteiro',
    descricao: 'Faça entregas e ganhe dinheiro',
    documentos: [
      { id: 'cpf', label: 'CPF válido', icon: <FileText size={20} />, obrigatorio: true },
      { id: 'cnh', label: 'CNH válida (categoria adequada)', icon: <Car size={20} />, obrigatorio: true },
      { id: 'selfie_cnh', label: 'Selfie com CNH', icon: <Camera size={20} />, obrigatorio: true },
      { id: 'veiculo_doc', label: 'Documento do veículo (CRLV)', icon: <ClipboardList size={20} />, obrigatorio: true },
      { id: 'veiculo_fotos', label: 'Fotos do veículo (4 ângulos)', icon: <Image size={20} />, obrigatorio: true },
      { id: 'conta_bancaria', label: 'Dados bancários para recebimento', icon: <Landmark size={20} />, obrigatorio: true },
    ],
    beneficios: ['Aceitar corridas', 'Ganhos diários', 'Histórico de entregas', 'Avaliações'],
  },
};

// Cor + tela inicial + se exige KYC pra cada perfil.
export const PERFIL_CONFIG = {
  locatario: { label: 'Particular', icon: <ShoppingCart size={18} />, cor: 'orange',  homeScreen: 'home',             kycObrigatorio: true },
  locador:   { label: 'Locador',    icon: <Store size={18} />, cor: 'indigo',  homeScreen: 'home',             kycObrigatorio: true },
  freteiro:  { label: 'Freteiro',   icon: <Truck size={18} />, cor: 'emerald', homeScreen: 'painel_freteiro',  kycObrigatorio: true },
};

// Menu Lateral (drawer) — atalhos navegacionais APENAS.
// Atividades operacionais (Locações, Anúncios, Pedidos Recebidos, Favoritos, Financeiro)
// vivem no Dashboard ("Meu Painel"). O drawer é um índice rápido pra qualquer tela.
export const MENU_CONSOLIDADO = {
  locatario: {
    principal: [
      { k: 'home', i: <Layers size={18} />, l: 'Início', d: 'Explorar e buscar', destaque: true },
      { k: 'pedidos', i: <Receipt size={18} />, l: 'Meus Pedidos', d: 'Pedidos feitos e em andamento' },
      { k: 'minhas_locacoes', i: <Calendar size={18} />, l: 'Minhas Locações', d: 'Ativas e histórico' },
      { k: 'carrinho', i: <ShoppingCart size={18} />, l: 'Carrinho', d: 'Itens para alugar' },
    ],
    comunicacao: [
      { k: 'chat', i: <MessageSquare size={18} />, l: 'Mensagens', d: 'Conversas' },
      { k: 'suporte', i: <Shield size={18} />, l: 'Ajuda', d: 'Suporte e disputas' },
    ],
  },
  locador: {
    painel: [
      { k: 'painel_locador', i: <BarChart3 size={18} />, l: 'Painel', d: 'Visão geral', destaque: true },
      { k: 'home', i: <Layers size={18} />, l: 'Início', d: 'Explorar e buscar' },
      { k: 'locacoes_central', i: <Bell size={18} />, l: 'Pedidos Recebidos', d: 'Solicitações e pendentes', badge: '2' },
      { k: 'meus_anuncios', i: <Tag size={18} />, l: 'Meus Anúncios', d: 'Gerenciar e editar itens' },
      { k: 'locacoes_central', i: <Calendar size={18} />, l: 'Locações dos meus itens', d: 'Em uso e devoluções' },
    ],
    comunicacao: [
      { k: 'chat', i: <MessageSquare size={18} />, l: 'Mensagens', d: 'Conversas' },
      { k: 'suporte', i: <Shield size={18} />, l: 'Ajuda', d: 'Suporte e disputas' },
    ],
  },
  freteiro: {
    // TUDO do freteiro vive dentro do Freteiro App (abas próprias estilo
    // Uber Driver) — o drawer só tem a porta de entrada. Nada espalhado.
    painel: [
      { k: 'painel_freteiro', i: <Navigation size={18} />, l: 'Freteiro App', d: 'Mapa, corridas, ganhos e conta', destaque: true },
    ],
    comunicacao: [
      { k: 'chat', i: <MessageSquare size={18} />, l: 'Mensagens', d: 'Conversas' },
      { k: 'suporte', i: <Shield size={18} />, l: 'Ajuda', d: 'Suporte e disputas' },
    ],
  },
  // Conta: identidade do usuário (mesmo pra todos os perfis)
  compartilhado: {
    conta: [
      { k: 'perfil', i: <User size={18} />, l: 'Meu Perfil', d: 'Identidade, empresas e configurações' },
    ],
  },
};
